import React, { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { SAVE_HOME_SECTION } from "./graphql";
import {
  SECTION_TYPES, CARD_STYLES, AUDIENCES, parseJson, toLocalInput, fromLocalInput,
} from "./labels";

const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1 text-sm">
    <span className="font-medium text-gray-800">{label}</span>
    {children}
    {hint && <span className="text-xs text-gray-500">{hint}</span>}
  </label>
);

const input = "border rounded-md px-3 py-2 text-sm w-full";
const lines = (text) => text.split("\n").map((l) => l.trim()).filter(Boolean);

/**
 * Add or edit one home section. Type-specific settings are plain fields;
 * anything else can still go in the Advanced JSON box.
 */
const SectionForm = ({ layoutId, layoutGender, section, collections, onClose, onSaved }) => {
  const editing = Boolean(section?.id);
  const initialConfig = parseJson(section?.config);
  const [type, setType] = useState(section?.type || "SERVICE_RAIL");
  const [f, setF] = useState({
    title: section?.title || "",
    subtitle: section?.subtitle || "",
    badge: section?.badge || "",
    cardStyle: section?.cardStyle || "COMPACT",
    collectionId: section?.collectionId || "",
    visibleCount: section?.visibleCount || 4,
    seeAllLabel: section?.seeAllLabel || "",
    audience: section?.audience || "ALL",
    startAt: toLocalInput(section?.startAt),
    endAt: toLocalInput(section?.endAt),
    isActive: section ? section.isActive : true,
    minAppVersion: section?.minAppVersion || "",
  });
  const [cfg, setCfg] = useState({
    couponCodes: (initialConfig.couponCodes || []).join(", "),
    windowDays: initialConfig.windowDays || 30,
    max: initialConfig.max || 12,
    promises: (initialConfig.promises || []).join("\n"),
    brands: (initialConfig.brands || []).join(", "),
    body: initialConfig.body || "",
    ctaLabel: initialConfig.ctaLabel || "",
    rewardText: initialConfig.rewardText || "",
    withinDays: initialConfig.withinDays || 14,
    includePriceBuckets: Boolean(initialConfig.includePriceBuckets),
    // "Title | detail" per line
    steps: (initialConfig.steps || []).map((st) => (st.body ? `${st.title} | ${st.body}` : st.title)).join("\n"),
    // "Q: …" then "A: …", blank line between questions
    faqs: (initialConfig.items || []).map((it) => `Q: ${it.q}\nA: ${it.a}`).join("\n\n"),
  });
  const [advanced, setAdvanced] = useState(section?.config && section.config !== "{}" ? section.config : "");
  const [error, setError] = useState("");
  const [save, { loading }] = useMutation(SAVE_HOME_SECTION);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const setC = (k) => (e) => setCfg({ ...cfg, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const help = SECTION_TYPES[type]?.help;
  // Only collections for this layout's tab (Salon or Bridal), plus any without a tab.
  const activeCollections = useMemo(
    () => collections.filter((c) => {
      const g = parseJson(c.rule).gender;
      return c.isActive && (!g || g === layoutGender);
    }),
    [collections, layoutGender],
  );

  const buildConfig = () => {
    const base = parseJson(advanced);
    switch (type) {
      case "COUPONS": {
        const codes = cfg.couponCodes.split(",").map((c) => c.trim().toUpperCase()).filter(Boolean);
        return { ...base, couponCodes: codes.length ? codes : undefined };
      }
      case "MOST_BOOKED":
        return { ...base, windowDays: Number(cfg.windowDays), max: Number(cfg.max) };
      case "TRUST":
        return { ...base, promises: lines(cfg.promises), brands: cfg.brands.split(",").map((b) => b.trim()).filter(Boolean) };
      case "CONSULT":
        return { ...base, body: cfg.body, ctaLabel: cfg.ctaLabel };
      case "RATING_PROMPT":
        return { ...base, rewardText: cfg.rewardText, withinDays: Number(cfg.withinDays) };
      case "CATEGORY_GRID":
        return { ...base, includePriceBuckets: cfg.includePriceBuckets || undefined };
      case "JOURNEY":
        return {
          ...base,
          steps: lines(cfg.steps).map((l) => {
            const [title, ...rest] = l.split("|");
            return { title: title.trim(), body: rest.join("|").trim() || undefined };
          }),
        };
      case "FAQ":
        return {
          ...base,
          items: cfg.faqs.split(/\n\s*\n/).map((block) => {
            const q = (block.match(/^\s*Q:\s*(.+)$/m) || [])[1];
            const a = (block.match(/^\s*A:\s*([\s\S]+)$/m) || [])[1];
            return q && a ? { q: q.trim(), a: a.replace(/\s+/g, " ").trim() } : null;
          }).filter(Boolean),
        };
      default:
        return base;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (advanced.trim()) {
      try { JSON.parse(advanced); } catch (err) { setError("Advanced settings must be valid JSON."); return; }
    }
    if (type === "SERVICE_RAIL" && !f.collectionId) { setError("Choose a collection for this rail."); return; }
    if (type === "JOURNEY" && !lines(cfg.steps).length) { setError("Add at least one step."); return; }
    if (type === "FAQ" && !(buildConfig().items || []).length) { setError("Add at least one question, as Q: … and A: … lines."); return; }
    const data = {
      id: section?.id,
      layoutId,
      type,
      title: f.title || null,
      subtitle: f.subtitle || null,
      badge: f.badge || null,
      cardStyle: ["SERVICE_RAIL", "BOOK_AGAIN"].includes(type) ? f.cardStyle : null,
      collectionId: type === "SERVICE_RAIL" ? f.collectionId : null,
      config: JSON.stringify(buildConfig()),
      visibleCount: Number(f.visibleCount),
      seeAllLabel: f.seeAllLabel || null,
      audience: f.audience,
      startAt: fromLocalInput(f.startAt),
      endAt: fromLocalInput(f.endAt),
      isActive: f.isActive,
      minAppVersion: f.minAppVersion || null,
    };
    const res = await save({ variables: { data } });
    const out = res.data?.saveHomeSection;
    if (out?.status !== "SUCCESS") { setError(out?.message || "Could not save the section."); return; }
    onSaved();
  };

  const showsCards = ["SERVICE_RAIL", "BOOK_AGAIN", "MOST_BOOKED", "CATEGORY_GRID", "COUPONS", "HERO_CAROUSEL", "DEAL", "OCCASIONS"].includes(type);

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-lg font-semibold">{editing ? "Edit section" : "Add section"}</h2>
        <button type="button" onClick={onClose} className="text-gray-500 hover:text-black text-xl leading-none" aria-label="Close">×</button>
      </div>

      <Field label="Section type" hint={help}>
        <select className={input} value={type} onChange={(e) => setType(e.target.value)} disabled={editing}>
          {Object.entries(SECTION_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </Field>

      {type === "SERVICE_RAIL" && (
        <Field label="Collection" hint="The services this rail shows. Create collections in the Collections tab.">
          <select className={input} value={f.collectionId} onChange={set("collectionId")}>
            <option value="">Choose a collection…</option>
            {activeCollections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Title" hint="Shown above the section. Leave empty for strips that have no heading.">
          <input className={input} value={f.title} onChange={set("title")} placeholder="Packages under ₹800" />
        </Field>
        <Field label="Subtitle" hint="One short line, under 40 characters.">
          <input className={input} value={f.subtitle} onChange={set("subtitle")} placeholder="Pick any 5 · most booked first" maxLength={60} />
        </Field>
      </div>

      {["SERVICE_RAIL", "BOOK_AGAIN"].includes(type) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Card style">
            <select className={input} value={f.cardStyle} onChange={set("cardStyle")}>
              {Object.entries(CARD_STYLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="Badge on the first card" hint="For example BESTSELLER or NEW. Optional.">
            <input className={input} value={f.badge} onChange={set("badge")} placeholder="BESTSELLER" maxLength={16} />
          </Field>
        </div>
      )}

      {showsCards && (
        <Field label="Items shown on the home page" hint="The rest appear under See all.">
          <select className={input} value={f.visibleCount} onChange={set("visibleCount")}>
            {[1, 3, 4, 6, 8, 12].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </Field>
      )}

      {type === "COUPONS" && (
        <Field label="Only these codes (optional)" hint="Comma-separated, in the order to show. Leave empty to show every visible code.">
          <input className={input} value={cfg.couponCodes} onChange={setC("couponCodes")} placeholder="WELCOME50, GLOWWAVE" />
        </Field>
      )}
      {type === "MOST_BOOKED" && (
        <div className="grid grid-cols-2 gap-4">
          <Field label="Count bookings from">
            <select className={input} value={cfg.windowDays} onChange={setC("windowDays")}>
              <option value={30}>Last 30 days</option>
              <option value={7}>Last 7 days</option>
            </select>
          </Field>
          <Field label="Services in See all">
            <input type="number" min={4} max={30} className={input} value={cfg.max} onChange={setC("max")} />
          </Field>
        </div>
      )}
      {type === "TRUST" && (
        <>
          <Field label="Promises (one per line)" hint="Only promises the service actually keeps.">
            <textarea rows={4} className={input} value={cfg.promises} onChange={setC("promises")} />
          </Field>
          <Field label="Brands (comma-separated)">
            <input className={input} value={cfg.brands} onChange={setC("brands")} placeholder="VLCC, O3+, Aroma" />
          </Field>
        </>
      )}
      {type === "CONSULT" && (
        <>
          <Field label="Text"><textarea rows={3} className={input} value={cfg.body} onChange={setC("body")} /></Field>
          <Field label="Button label"><input className={input} value={cfg.ctaLabel} onChange={setC("ctaLabel")} placeholder="Book free consultation" /></Field>
        </>
      )}
      {type === "RATING_PROMPT" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Reward line" hint="Only if the reward is real."><input className={input} value={cfg.rewardText} onChange={setC("rewardText")} placeholder="Rate it and get ₹20 off your next booking" /></Field>
          <Field label="Ask for bookings completed in the last (days)"><input type="number" min={1} max={60} className={input} value={cfg.withinDays} onChange={setC("withinDays")} /></Field>
        </div>
      )}
      {type === "JOURNEY" && (
        <Field label="Steps (one per line)" hint="Title | one line of detail. Example: Free consultation | A 15-minute call to plan your looks">
          <textarea rows={4} className={input} value={cfg.steps} onChange={setC("steps")} />
        </Field>
      )}
      {type === "FAQ" && (
        <Field label="Questions" hint="Write Q: and A: lines, with a blank line between questions.">
          <textarea rows={8} className={`${input} font-mono text-xs`} value={cfg.faqs} onChange={setC("faqs")} placeholder={"Q: Do you bring the products?\nA: Yes, sealed and opened in front of you."} />
        </Field>
      )}
      {type === "CATEGORY_GRID" && (
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={cfg.includePriceBuckets} onChange={setC("includePriceBuckets")} /> Also show price-bucket categories (Under ₹800…)</label>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Who sees it">
          <select className={input} value={f.audience} onChange={set("audience")}>
            {Object.entries(AUDIENCES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="See all label (optional)">
          <input className={input} value={f.seeAllLabel} onChange={set("seeAllLabel")} placeholder="See all" />
        </Field>
        <Field label="Show from (optional)"><input type="datetime-local" className={input} value={f.startAt} onChange={set("startAt")} /></Field>
        <Field label="Show until (optional)"><input type="datetime-local" className={input} value={f.endAt} onChange={set("endAt")} /></Field>
      </div>

      <details className="text-sm">
        <summary className="cursor-pointer text-gray-600">Advanced</summary>
        <div className="flex flex-col gap-4 mt-3">
          <Field label="Minimum app version" hint="Hide this section from older app builds, e.g. 3.1.0.">
            <input className={input} value={f.minAppVersion} onChange={set("minAppVersion")} />
          </Field>
          <Field label="Extra settings (JSON)" hint='Merged with the fields above, e.g. {"bannerIds": ["…"]} for the carousel.'>
            <textarea rows={3} className={`${input} font-mono text-xs`} value={advanced} onChange={(e) => setAdvanced(e.target.value)} />
          </Field>
        </div>
      </details>

      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.isActive} onChange={set("isActive")} /> Section is on</label>

      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-gray-100">Cancel</button>
        <button type="submit" disabled={loading} className="px-5 py-2 rounded-md bg-[#FBB033] font-semibold disabled:opacity-60">
          {loading ? "Saving…" : editing ? "Save section" : "Add section"}
        </button>
      </div>
    </form>
  );
};

export default SectionForm;
